import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listSkills } from "../services/skills";
import { SKILL_STATUSES } from "../types/skill";
import type { SkillWithStats } from "../types/skill";
import { StatusBadge } from "../components/StatusBadge";

type SortKey = "updatedAt" | "name" | "status" | "documents";
type EnabledFilter = "all" | "enabled" | "disabled";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function SkillListPage() {
  const navigate = useNavigate();
  const [skills, setSkills] = useState<SkillWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [enabled, setEnabled] = useState<EnabledFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setSkills(await listSkills());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load skills");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = skills.filter((s) => {
      if (status !== "all" && s.status !== status) return false;
      if (enabled === "enabled" && s.enabled === false) return false;
      if (enabled === "disabled" && s.enabled !== false) return false;
      if (q) {
        const hay = `${s.id} ${s.name} ${s.description}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "name":
          cmp = (a.name || a.id).localeCompare(b.name || b.id);
          break;
        case "status":
          cmp = a.status.localeCompare(b.status);
          break;
        case "documents":
          cmp = a.documentCount - b.documentCount;
          break;
        default:
          cmp = (a.updatedAt || "").localeCompare(b.updatedAt || "");
      }
      return cmp * dir;
    });
    return rows;
  }, [skills, search, status, enabled, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  return (
    <div className="list-page">
      <div className="list-toolbar">
        <h1>Skills</h1>
        <input
          className="search-box"
          type="search"
          placeholder="Search id, name, description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="spacer" />
        <button className="btn" onClick={load}>↻ Refresh</button>
        <button className="btn btn-primary" onClick={() => navigate("/skills/new")}>+ New skill</button>
      </div>

      <div className="filters">
        <div className="filter">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All statuses</option>
            {SKILL_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="filter">
          <label>Enabled</label>
          <select value={enabled} onChange={(e) => setEnabled(e.target.value as EnabledFilter)}>
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="state-box">Loading skills…</div>
      ) : error ? (
        <div className="state-box error">{error}</div>
      ) : (
        <>
          <div className="result-count">
            {filtered.length} of {skills.length} skill{skills.length === 1 ? "" : "s"}
          </div>
          {filtered.length === 0 ? (
            <div className="state-box">
              No skills match your filters. Skills are topic packages (financial wellness,
              mental wellness, …) with a searchable knowledge base, attachable to any companion.
            </div>
          ) : (
            <table className="guide-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort("name")}>Skill{arrow("name")}</th>
                  <th onClick={() => toggleSort("status")}>Status{arrow("status")}</th>
                  <th onClick={() => toggleSort("documents")}>Knowledge base{arrow("documents")}</th>
                  <th onClick={() => toggleSort("updatedAt")}>Updated{arrow("updatedAt")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="guide-row" onClick={() => navigate(`/skills/${s.id}`)}>
                    <td>
                      <div className="guide-name">
                        <span
                          className="accent-dot"
                          style={s.accentColor ? { background: s.accentColor } : undefined}
                        />
                        <div>
                          <div className="name">{s.icon ? `${s.icon} ` : ""}{s.name || s.id}</div>
                          <div className="role">{s.description || s.id}</div>
                        </div>
                      </div>
                    </td>
                    <td><StatusBadge status={s.status} enabled={s.enabled} /></td>
                    <td className="voice-cell">
                      {s.documentCount === 0
                        ? "empty"
                        : `${s.readyDocumentCount}/${s.documentCount} docs ready · ${s.chunkCount} chunks`}
                    </td>
                    <td className="voice-cell">{formatDate(s.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
